import { hasAnyPermission, isSuperAdmin } from '@/features/auth';
import type { AuthUser } from '@/features/auth';

/**
 * Permissions each dashboard metric's endpoint actually accepts — mirrors the
 * `role_or_permission` middleware on its route in `backend/routes/api/admin/`.
 *
 * A section of this dashboard is one coarse OR-group over several separately
 * guarded endpoints, so section visibility can never gate the requests: a role
 * holding only `page_indexes.view` made the External Lab section visible, which
 * fired `/registrations/metrics` (`registrations.view|supervisor.view`) and
 * `/review/metrics` (`review.view`), collected two 403s and the "User does not
 * have any of the necessary access rights." toast, and rendered a dashboard of
 * zeros. Gate every query — and the card that reads it — on its own entry here.
 */
export const DASHBOARD_METRIC_PERMISSIONS = {
  /** GET /registrations/metrics */
  registrations: ['registrations.view', 'supervisor.view'],
  /** GET /boxes/metrics */
  boxes: ['boxes.view', 'supervisor.view'],
  /** GET /shipments/metrics */
  shipments: ['shipments.view', 'supervisor.view'],
  /** GET /users/metrics */
  users: ['users.view'],
  /** GET /sticking/metrics */
  indexing: ['print_batches.view'],
  /** GET /review/metrics */
  review: ['review.view'],
  /** GET /reception/shipments/metrics */
  reception: ['reception.view'],
  /** GET /sorting/sessions/metrics */
  sorting: ['sorting.view'],
  /** GET /handover-scanning/metrics */
  handover: ['handover_scanning.view'],
  /** GET /photo-scanning/metrics */
  photoScanning: ['photo_scanning.view'],
  /** GET /qr-review/metrics */
  qrReview: ['qr_review.view', 'qa3_ocr.view'],
  /** GET /ocr-qa/1/metrics */
  ocrQa1: ['qa1_ocr.view'],
  /** GET /ocr-qa/2/metrics */
  ocrQa2: ['qa2_ocr.view'],
  /** GET /ocr-qa/3/metrics */
  ocrQa3: ['qa3_ocr.view'],
  /** GET /shelves/stats */
  shelves: ['shelves.view', 'warehouse.operate', 'supervisor.view'],
  /** GET /storage-locations/stats */
  storageLocations: ['storage_locations.view'],
  /** GET /devices/metrics */
  devices: ['devices.view'],
  /** GET /clients/metrics */
  clients: ['clients.view']
} as const satisfies Record<string, readonly string[]>;

export type DashboardMetricKey = keyof typeof DASHBOARD_METRIC_PERMISSIONS;

/** Whether this user's permissions can actually load the metric's endpoint. */
export function canLoadMetric(
  user: AuthUser | null,
  metric: DashboardMetricKey
): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;

  return hasAnyPermission(user, [...DASHBOARD_METRIC_PERMISSIONS[metric]]);
}
